import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Container,
  Alert,
  CircularProgress,
  ThemeProvider,
  CssBaseline,
} from '@mui/material';
import {
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Pool as PoolIcon,
} from '@mui/icons-material';
import { Outlet, useOutletContext } from 'react-router-dom';
import { createPortalTheme, defaultTheme } from '../styles/theme';
import { PortalHeader } from '../components/PortalHeader';

interface AccountBranding {
  businessName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  businessPhone: string | null;
  businessEmail: string | null;
  showIdealChemicalRanges: boolean;
  showTechnicianAndTiming: boolean;
}

interface SampleReportContext {
  showIdealChemicalRanges: boolean;
  showTechnicianAndTiming: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

// ─── Sample Data ──────────────────────────────────────────────

const sampleServiceData = {
  serviceDate: (() => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    return d.toISOString();
  })(),
  arrivedAt: (() => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    d.setHours(9, 15, 0);
    return d.toISOString();
  })(),
  completedAt: (() => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    d.setHours(9, 42, 0);
    return d.toISOString();
  })(),
  durationMinutes: 27,
  status: 'completed',
  technicianName: 'Mike R.',
  property: { name: '4821 Sunset Dr', address: '4821 Sunset Dr, Austin, TX 78745' },
  bodyOfWater: { name: 'Main Pool', type: 'pool' },
  techNotes: 'Pool looking great this week. Backwashed the filter — pressure was at 22 PSI, brought it down to 12. Noticed a small crack forming on the west-side coping tile, took a photo. Nothing urgent but worth monitoring. Added liquid chlorine to bring FC up from 1.5 to 3.0.',
  chemicalReading: {
    chlorine: 3.0,
    ph: 7.4,
    alkalinity: 90,
    calciumHardness: 320,
    cyanuricAcid: 45,
    salt: 3200,
    waterTemp: 84,
    phosphates: null,
    psi: 12,
    chemicalsAdded: [
      { name: 'Liquid Chlorine', amount: '1', unit: 'gal' },
    ],
  },
  checklistItems: [
    { label: 'Skim surface', completed: true },
    { label: 'Brush walls & steps', completed: true },
    { label: 'Vacuum pool floor', completed: true },
    { label: 'Empty skimmer baskets', completed: true },
    { label: 'Empty pump basket', completed: true },
    { label: 'Check & adjust water level', completed: true },
    { label: 'Backwash filter', completed: true },
    { label: 'Test water chemistry', completed: true },
    { label: 'Inspect equipment', completed: true },
    { label: 'Clean tile line', completed: true },
  ],
};

const sampleInspectionData = {
  serviceDate: (() => {
    const d = new Date();
    d.setDate(d.getDate() - 5);
    return d.toISOString();
  })(),
  arrivedAt: (() => {
    const d = new Date();
    d.setDate(d.getDate() - 5);
    d.setHours(10, 0, 0);
    return d.toISOString();
  })(),
  completedAt: (() => {
    const d = new Date();
    d.setDate(d.getDate() - 5);
    d.setHours(11, 15, 0);
    return d.toISOString();
  })(),
  durationMinutes: 75,
  technicianName: 'Mike R.',
  property: { name: '4821 Sunset Dr', address: '4821 Sunset Dr, Austin, TX 78745' },
  bodyOfWater: { name: 'Main Pool', type: 'pool' },
  verdict: 'needs_attention',
  sections: [
    {
      label: 'Perimeter Fencing',
      items: [
        { label: 'Fence condition', condition: 'satisfactory' },
        { label: 'Gate self-closing/self-latching', condition: 'satisfactory' },
        { label: 'Gate hardware intact', condition: 'satisfactory' },
        { label: 'Fence height adequate', condition: 'satisfactory' },
      ],
    },
    {
      label: 'Pool Decking',
      items: [
        { label: 'Deck surface condition', condition: 'satisfactory' },
        { label: 'No significant cracks or trip hazards', condition: 'satisfactory' },
        { label: 'Proper drainage away from pool', condition: 'satisfactory' },
        { label: 'Coping condition', condition: 'needs_attention', notes: 'Small crack forming on west-side coping tile. Not structural but should be repaired within the next 6 months to prevent water intrusion.' },
      ],
    },
    {
      label: 'Pool Surface',
      items: [
        { label: 'Surface condition (plaster/vinyl/fiberglass)', condition: 'satisfactory' },
        { label: 'No significant staining or etching', condition: 'satisfactory' },
        { label: 'Main drain/VGB cover present and secure', condition: 'satisfactory' },
        { label: 'Drain cover in good condition (no cracks)', condition: 'satisfactory' },
        { label: 'Steps/benches condition', condition: 'satisfactory' },
        { label: 'Water clarity', condition: 'satisfactory' },
      ],
    },
    {
      label: 'Pool Equipment',
      items: [
        { label: 'Pump operational', condition: 'satisfactory' },
        { label: 'No visible leaks at pump', condition: 'satisfactory' },
        { label: 'Pump basket in good condition', condition: 'satisfactory' },
        { label: 'Filter condition', condition: 'satisfactory' },
        { label: 'Filter pressure within normal range', condition: 'satisfactory' },
        { label: 'Heater operational', condition: 'needs_attention', notes: 'Heater ignites on second attempt. Igniter may need replacement soon. Functional for now but recommend scheduling service before next winter.' },
        { label: 'Sanitizer system (salt/chlorinator) operational', condition: 'satisfactory' },
      ],
    },
    {
      label: 'Pool Lighting',
      items: [
        { label: 'Pool lights operational', condition: 'satisfactory' },
        { label: 'Light lenses intact', condition: 'satisfactory' },
        { label: 'GFCI protection present', condition: 'satisfactory' },
      ],
    },
    {
      label: 'Water Chemistry',
      items: [
        { label: 'Water chemistry in acceptable range', condition: 'satisfactory' },
        { label: 'pH level (7.2-7.6)', condition: 'satisfactory' },
        { label: 'Free chlorine level (1-3 ppm)', condition: 'satisfactory' },
        { label: 'Total alkalinity (80-120 ppm)', condition: 'satisfactory' },
      ],
    },
    {
      label: 'Safety Equipment',
      items: [
        { label: 'Life ring / reaching pole present', condition: 'satisfactory' },
        { label: 'Depth markings visible', condition: 'satisfactory' },
      ],
    },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────

const chemicalLabels: Record<string, { label: string; unit: string; ideal: string }> = {
  chlorine: { label: 'Free Chlorine', unit: 'ppm', ideal: '1-3' },
  ph: { label: 'pH', unit: '', ideal: '7.2-7.6' },
  alkalinity: { label: 'Total Alkalinity', unit: 'ppm', ideal: '80-120' },
  calciumHardness: { label: 'Calcium Hardness', unit: 'ppm', ideal: '200-400' },
  cyanuricAcid: { label: 'Cyanuric Acid', unit: 'ppm', ideal: '30-50' },
  salt: { label: 'Salt', unit: 'ppm', ideal: '2700-3400' },
  waterTemp: { label: 'Water Temp', unit: '\u00B0F', ideal: '-' },
  phosphates: { label: 'Phosphates', unit: 'ppb', ideal: '<100' },
  psi: { label: 'Filter PSI', unit: 'psi', ideal: '-' },
};

const conditionColors: Record<string, { bg: string; text: string; label: string }> = {
  satisfactory: { bg: '#e8f5e9', text: '#2e7d32', label: 'Satisfactory' },
  needs_attention: { bg: '#fff3e0', text: '#e65100', label: 'Needs Attention' },
  safety_issue: { bg: '#ffebee', text: '#c62828', label: 'Safety Issue' },
};

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const formatTime = (dateStr: string) =>
  new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

// ─── Service Report Page ──────────────────────────────────────

export const SampleServiceReport: React.FC = () => {
  const { showIdealChemicalRanges, showTechnicianAndTiming } =
    useOutletContext<SampleReportContext>();

  return (
    <>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h5">
              {formatDate(sampleServiceData.serviceDate)}
            </Typography>
            <Chip label="Completed" color="success" />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">Property</Typography>
              <Typography>{sampleServiceData.property.name}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Body of Water</Typography>
              <Typography>{sampleServiceData.bodyOfWater.name}</Typography>
            </Box>
            {showTechnicianAndTiming && (
              <>
                <Box>
                  <Typography variant="body2" color="text.secondary">Technician</Typography>
                  <Typography>{sampleServiceData.technicianName}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Service Time</Typography>
                  <Typography>
                    {formatTime(sampleServiceData.arrivedAt)} - {formatTime(sampleServiceData.completedAt)}
                    {` (${sampleServiceData.durationMinutes} min)`}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Tech Notes */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Technician Notes</Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>{sampleServiceData.techNotes}</Typography>
        </CardContent>
      </Card>

      {/* Checklist + Chemical Readings + Chemicals Added — 3 columns */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Service Checklist</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sampleServiceData.checklistItems.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Chemical Readings</Typography>
            <Table size="small">
              <TableBody>
                {Object.entries(chemicalLabels).map(([key, { label, unit, ideal }]) => {
                  const value = sampleServiceData.chemicalReading[key as keyof typeof sampleServiceData.chemicalReading];
                  if (value === null || value === undefined) return null;
                  return (
                    <TableRow key={key}>
                      <TableCell sx={{ fontWeight: 500 }}>{label}</TableCell>
                      <TableCell align="right">
                        {parseFloat(Number(value).toFixed(1))} {unit}
                      </TableCell>
                      {showIdealChemicalRanges && (
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>
                          Ideal: {ideal}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Chemicals Added</Typography>
            {sampleServiceData.chemicalReading.chemicalsAdded.map((c: any, i: number) => (
              <Typography key={i} variant="body2" color="text.secondary">
                {c.name}: {c.amount} {c.unit}
              </Typography>
            ))}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

// ─── Inspection Report Page ───────────────────────────────────

export const SampleInspectionReport: React.FC = () => {
  const totalItems = sampleInspectionData.sections.reduce((sum, s) => sum + s.items.length, 0);
  const issueItems = sampleInspectionData.sections.reduce(
    (sum, s) => sum + s.items.filter((i) => i.condition !== 'satisfactory').length,
    0,
  );

  return (
    <>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h5">
              Pool Inspection - {formatDate(sampleInspectionData.serviceDate)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">Property</Typography>
              <Typography>{sampleInspectionData.property.name}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Inspector</Typography>
              <Typography>{sampleInspectionData.technicianName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Duration</Typography>
              <Typography>{sampleInspectionData.durationMinutes} minutes</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Items Inspected</Typography>
              <Typography>{totalItems}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Verdict */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: conditionColors.needs_attention.bg,
            }}
          >
            <WarningIcon sx={{ color: conditionColors.needs_attention.text, fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ color: conditionColors.needs_attention.text }}>
                Needs Attention
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {issueItems} item{issueItems !== 1 ? 's' : ''} require attention. {totalItems - issueItems} items satisfactory.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Sections */}
      {sampleInspectionData.sections.map((section, sIndex) => {
        const sectionHasIssues = section.items.some((i) => i.condition !== 'satisfactory');
        return (
          <Card key={sIndex} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6">{section.label}</Typography>
                {!sectionHasIssues && (
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {section.items.map((item, iIndex) => {
                  const colors = conditionColors[item.condition] || conditionColors.satisfactory;
                  return (
                    <Box key={iIndex}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={colors.label}
                          size="small"
                          sx={{
                            bgcolor: colors.bg,
                            color: colors.text,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                          }}
                        />
                        <Typography variant="body2">{item.label}</Typography>
                      </Box>
                      {'notes' in item && item.notes && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 4, mt: 0.5, fontStyle: 'italic' }}
                        >
                          {item.notes}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </>
  );
};

// ─── Shared Layout ────────────────────────────────────────────

export const SampleReportLayout: React.FC = () => {
  const [branding, setBranding] = useState<AccountBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useMemo(() => {
    if (branding?.primaryColor) {
      return createPortalTheme(branding.primaryColor, branding.secondaryColor);
    }
    return defaultTheme;
  }, [branding?.primaryColor, branding?.secondaryColor]);

  useEffect(() => {
    const hostname = window.location.hostname;
    const sub = hostname.endsWith('.pooldial.com')
      ? hostname.replace('.pooldial.com', '')
      : undefined;
    const subdomain = sub && sub !== 'portal' ? sub : undefined;

    if (!subdomain) {
      setError('No business found. This sample report requires a custom portal URL.');
      setLoading(false);
      return;
    }

    fetch(`${API_BASE_URL}/portal/sample-branding?subdomain=${encodeURIComponent(subdomain)}`)
      .then((res) => {
        if (!res.ok) throw new Error('Business not found');
        return res.json();
      })
      .then((data) => {
        setBranding(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header */}
        <PortalHeader businessName={branding?.businessName} logoUrl={branding?.logoUrl} />

        {/* Sample banner */}
        <Box
          sx={{
            mt: 3,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 1.5,
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            <PoolIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
            This is a sample report from {branding?.businessName}. Contact us to get started with regular pool service.
          </Typography>
        </Box>

        {/* Content */}
        <Container maxWidth="lg" sx={{ py: 3, flex: 1 }}>
          <Outlet context={{
            showIdealChemicalRanges: branding?.showIdealChemicalRanges ?? false,
            showTechnicianAndTiming: branding?.showTechnicianAndTiming ?? false,
          } satisfies SampleReportContext} />
        </Container>

        {/* Footer */}
        {(branding?.businessPhone || branding?.businessEmail) && (
          <Box
            component="footer"
            sx={{
              py: 3,
              px: 2,
              textAlign: 'center',
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Contact {branding.businessName}:{' '}
              {branding.businessPhone && (
                <Box component="a" href={`tel:${branding.businessPhone}`} sx={{ color: 'text.primary', textDecoration: 'none' }}>
                  {branding.businessPhone}
                </Box>
              )}
              {branding.businessPhone && branding.businessEmail && ' | '}
              {branding.businessEmail && (
                <Box component="a" href={`mailto:${branding.businessEmail}`} sx={{ color: 'text.primary', textDecoration: 'none' }}>
                  {branding.businessEmail}
                </Box>
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};
